/**
 * Custom Tooltip System
 * Creates tooltips for elements with data-label attributes in forms with custom-tooltip class
 */

(function () {
    'use strict';

    // SVG icon for tooltip trigger
    const TOOLTIP_ICON = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
    </svg>
  `;

    /**
     * Initialize tooltips for a container
     * @param {HTMLElement} container - Container element with custom-tooltip class
     */
    function initTooltips(container) {
        if (!container || container.hasAttribute('data-tooltips-initialized')) {
            return;
        }

        // Find all globo-description elements
        const descriptions = container.querySelectorAll('.globo-description');

        descriptions.forEach((description, index) => {
            // Get the description text content
            const tooltipText = description.textContent.trim();

            if (!tooltipText || tooltipText === '') {
                return;
            }

            // Find the closest form group or parent container
            const formGroup = description.closest('.globo-form-control, .globo-form-group, .form-group') || description.parentElement;

            if (!formGroup) {
                return;
            }

            // Find the label within the form group
            // Try multiple selectors to find the label
            let label = formGroup.querySelector('.classic-label.globo-label');
            if (!label) {
                label = formGroup.querySelector('.classic-label');
            }
            if (!label) {
                label = formGroup.querySelector('.globo-label');
            }

            if (!label) {
                return;
            }

            // Check if tooltip already exists
            if (label.querySelector('.tooltip-container')) {
                return;
            }

            // Create unique ID for tooltip
            const tooltipId = `tooltip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            // Create tooltip container
            const tooltipContainer = document.createElement('span');
            tooltipContainer.className = 'tooltip-container';

            // Create tooltip trigger button
            const trigger = document.createElement('button');
            trigger.type = 'button';
            trigger.className = 'tooltip-trigger';
            trigger.setAttribute('aria-label', 'More information');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.setAttribute('aria-describedby', tooltipId);
            trigger.innerHTML = TOOLTIP_ICON;

            // Create tooltip content
            const content = document.createElement('span');
            content.id = tooltipId;
            content.className = 'tooltip-content';
            content.setAttribute('role', 'tooltip');
            content.textContent = tooltipText;

            // Assemble tooltip - only trigger stays in label
            tooltipContainer.appendChild(trigger);
            
            // Append tooltip container to label
            label.appendChild(tooltipContainer);
            
            // Move tooltip content to body for fixed positioning
            document.body.appendChild(content);
            
            // Store reference to trigger in content
            const triggerId = trigger.id || `trigger-${Date.now()}`;
            content.setAttribute('data-trigger-id', triggerId);
            if (!trigger.id) {
                trigger.id = triggerId;
            }

            // Add event listeners
            setupTooltipEvents(trigger, content);
        });

        container.setAttribute('data-tooltips-initialized', 'true');
    }

    /**
     * Setup event listeners for tooltip
     * @param {HTMLElement} trigger - Tooltip trigger button
     * @param {HTMLElement} content - Tooltip content element
     */
    function setupTooltipEvents(trigger, content) {
        let isActive = false;

        /**
         * Show tooltip
         */
        function showTooltip() {
            isActive = true;
            trigger.classList.add('active');
            trigger.setAttribute('aria-expanded', 'true');
            content.classList.add('tooltip-visible');
            closeOtherTooltips(trigger);
            adjustTooltipPosition(trigger, content);
        }

        /**
         * Hide tooltip
         */
        function hideTooltip() {
            isActive = false;
            trigger.classList.remove('active');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.removeAttribute('data-pinned');
            content.classList.remove('tooltip-visible');
        }

        // Click handler (toggle - pins tooltip open on desktop, shows on mobile)
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (isActive) {
                hideTooltip();
            } else {
                showTooltip();
                // Mark as explicitly activated (pinned)
                trigger.setAttribute('data-pinned', 'true');
            }
        });

        // Keyboard support
        trigger.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                trigger.click();
            } else if (e.key === 'Escape' && isActive) {
                e.preventDefault();
                hideTooltip();
            }
        });

        // Mouse hover support (desktop)
        if (window.matchMedia('(hover: hover)').matches) {
            let hideTimeout = 0;
            
            trigger.addEventListener('mouseenter', () => {
                // Clear any pending hide
                if (hideTimeout) {
                    clearTimeout(hideTimeout);
                    hideTimeout = 0;
                }
                showTooltip();
            });
            
            trigger.addEventListener('mouseleave', () => {
                // Delay hide to allow moving to tooltip content
                hideTimeout = window.setTimeout(() => {
                    // Only hide if not hovering over content and not pinned
                    if (!content.matches(':hover') && !trigger.hasAttribute('data-pinned')) {
                        hideTooltip();
                    }
                }, 100);
            });
            
            // Keep tooltip open when hovering over it
            content.addEventListener('mouseenter', () => {
                if (hideTimeout) {
                    clearTimeout(hideTimeout);
                    hideTimeout = 0;
                }
            });
            
            content.addEventListener('mouseleave', () => {
                // Hide when leaving tooltip content (unless pinned)
                if (!trigger.hasAttribute('data-pinned')) {
                    hideTooltip();
                }
            });
        }

        // Close tooltip when clicking outside
        document.addEventListener('click', (e) => {
            const target = e.target;
            if (target instanceof Node && !trigger.contains(target) && !content.contains(target)) {
                hideTooltip();
            }
        });
    }

    /**
     * Close all other active tooltips
     * @param {HTMLElement} currentTrigger - Currently active trigger
     */
    function closeOtherTooltips(currentTrigger) {
        const allTriggers = document.querySelectorAll('.tooltip-trigger.active');
        const allContents = document.querySelectorAll('.tooltip-content.tooltip-visible');
        
        allTriggers.forEach((trigger) => {
            if (trigger !== currentTrigger) {
                trigger.classList.remove('active');
                trigger.setAttribute('aria-expanded', 'false');
            }
        });
        
        allContents.forEach((content) => {
            const triggerId = content.getAttribute('data-trigger-id');
            if (triggerId !== currentTrigger.id) {
                content.classList.remove('tooltip-visible');
            }
        });
    }

    /**
     * Adjust tooltip position to prevent overflow
     * @param {HTMLElement} trigger - Tooltip trigger element
     * @param {HTMLElement} content - Tooltip content element
     */
    function adjustTooltipPosition(trigger, content) {
        // Reset classes
        content.classList.remove('tooltip--left', 'tooltip--right', 'tooltip--bottom');
        
        // Get trigger position relative to viewport
        const triggerRect = trigger.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 10;
        const arrowSize = 10;
        
        // Calculate center position
        const triggerCenterX = triggerRect.left + (triggerRect.width / 2);
        const triggerTop = triggerRect.top;
        
        // Position tooltip above trigger by default
        let tooltipLeft = triggerCenterX - (contentRect.width / 2);
        let tooltipTop = triggerTop - contentRect.height - arrowSize;
        
        // Check left edge
        if (tooltipLeft < padding) {
            tooltipLeft = padding;
            content.classList.add('tooltip--left');
        }
        
        // Check right edge  
        if (tooltipLeft + contentRect.width > viewportWidth - padding) {
            tooltipLeft = viewportWidth - contentRect.width - padding;
            content.classList.add('tooltip--right');
        }
        
        // Check top edge - if not enough space above, show below
        if (tooltipTop < padding) {
            tooltipTop = triggerRect.bottom + arrowSize;
            content.classList.add('tooltip--bottom');
        }
        
        // Set final position
        content.style.left = `${tooltipLeft}px`;
        content.style.top = `${tooltipTop}px`;
        content.style.transform = 'none';
        
        // Adjust arrow position to point to trigger center
        const arrowOffset = triggerCenterX - tooltipLeft;
        content.style.setProperty('--arrow-offset', `${arrowOffset}px`);
    }

  /**
   * Initialize all elements with custom-tooltip class
   */
  function initAllTooltips() {
    // Find all elements with custom-tooltip class (forms, divs, etc.)
    const containers = document.querySelectorAll('.custom-tooltip');

    // Process existing containers
    if (containers && containers.length > 0) {
      containers.forEach((container) => {
        if (container instanceof HTMLElement) {
          initTooltips(container);
        }
      });
    }

    // Always setup MutationObserver to watch for dynamically added elements
    if (window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node instanceof HTMLElement) {
              // Element node
              if (node.matches('.custom-tooltip')) {
                initTooltips(node);
              } else {
                const newContainers = node.querySelectorAll('.custom-tooltip');
                if (newContainers.length > 0) {
                  newContainers.forEach((container) => {
                    if (container instanceof HTMLElement) {
                      initTooltips(container);
                    }
                  });
                }
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllTooltips);
  } else {
    initAllTooltips();
  }

  // Re-initialize on page show (for browser back/forward)
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      initAllTooltips();
    }
  });
})();

